import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { X, Flag, Eye, EyeOff, Trash2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { auth } from '../../lib/firebase';
import { Challenge } from '../../types/challenges';
import { ContentReport } from '../../types/contentModeration';
import {
  subscribeToContentReports,
  updateReportStatus,
  hideContent,
  unhideContent,
  deleteContentResult,
  subscribeToChallenges
} from '../../lib/store';

interface AdminDashboardProps {
  onClose: () => void;
}

export function AdminDashboard({ onClose }: AdminDashboardProps) {
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [selectedTab, setSelectedTab] = useState<'pending' | 'reviewed'>('pending');
  const [selectedReport, setSelectedReport] = useState<ContentReport | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [user] = useAuthState(auth);

  useEffect(() => {
    if (!user || !auth.currentUser || user.email !== 'breadflowbybadabec@gmail.com') {
      if (user && user.email !== 'breadflowbybadabec@gmail.com') {
        onClose();
      }
      return;
    }

    const unsubscribeReports = subscribeToContentReports((updatedReports) => {
      setReports(updatedReports);
    });

    const unsubscribeChallenges = subscribeToChallenges((updatedChallenges) => {
      setChallenges(updatedChallenges);
    });

    return () => {
      unsubscribeReports();
      unsubscribeChallenges();
    };
  }, [user, onClose]);

  const pendingReports = reports.filter(r => r.status === 'pending');
  const reviewedReports = reports.filter(r => r.status === 'reviewed' || r.status === 'resolved' || r.status === 'dismissed');

  const getReportedContent = (report: ContentReport) => {
    const challenge = challenges.find(c => c.id === report.contentId.challengeId);
    if (!challenge) return null;

    const participant = challenge.participants.find(p => p.userId === report.contentId.participantId);
    return { challenge, participant };
  };

  const handleHideContent = async (report: ContentReport) => {
    try {
      setIsProcessing(true);
      await hideContent(report.contentId.challengeId, report.contentId.participantId);
      await updateReportStatus(report.id, 'resolved', 'hidden', adminNotes);
      setAdminNotes('');
      setSelectedReport(null);
    } catch (error) {
      console.error('Error hiding content:', error);
      alert('Failed to hide content');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnhideContent = async (report: ContentReport) => {
    try {
      setIsProcessing(true);
      await unhideContent(report.contentId.challengeId, report.contentId.participantId);
      await updateReportStatus(report.id, 'resolved', 'no_action', 'Content unhidden');
      setSelectedReport(null);
    } catch (error) {
      console.error('Error unhiding content:', error);
      alert('Failed to unhide content');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteContent = async (report: ContentReport) => {
    if (!confirm('Are you sure you want to permanently delete this content? This action cannot be undone.')) {
      return;
    }

    try {
      setIsProcessing(true);
      await deleteContentResult(report.contentId.challengeId, report.contentId.participantId);
      await updateReportStatus(report.id, 'resolved', 'deleted', adminNotes);
      setAdminNotes('');
      setSelectedReport(null);
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Failed to delete content');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDismissReport = async (report: ContentReport) => {
    try {
      setIsProcessing(true);
      await updateReportStatus(report.id, 'dismissed', 'no_action', adminNotes);
      setAdminNotes('');
      setSelectedReport(null);
    } catch (error) {
      console.error('Error dismissing report:', error);
      alert('Failed to dismiss report');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (report: ContentReport) => {
    const styles = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      reviewed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
      dismissed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs border ${styles[report.status]}`}>
        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
      </span>
    );
  };

  const getActionBadge = (action?: string) => {
    if (!action) return null;

    const styles = {
      hidden: 'bg-orange-500/20 text-orange-400',
      deleted: 'bg-red-500/20 text-red-400',
      no_action: 'bg-gray-500/20 text-gray-400'
    };

    const labels = {
      hidden: 'Hidden',
      deleted: 'Deleted',
      no_action: 'No Action'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs ${styles[action as keyof typeof styles]}`}>
        {labels[action as keyof typeof labels]}
      </span>
    );
  };

  const displayReports = selectedTab === 'pending' ? pendingReports : reviewedReports;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-7xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl text-white font-brand font-light italic">Content Moderation</h2>
              <p className="text-sm text-white/70 mt-1">
                Review and manage reported content
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setSelectedTab('pending')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedTab === 'pending'
                  ? 'bg-amber-400 text-black font-medium'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              Pending ({pendingReports.length})
            </button>
            <button
              onClick={() => setSelectedTab('reviewed')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedTab === 'reviewed'
                  ? 'bg-amber-400 text-black font-medium'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              Reviewed ({reviewedReports.length})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {displayReports.length === 0 ? (
            <div className="text-center text-white/70 py-12">
              <Flag className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No {selectedTab} reports</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {displayReports.map((report) => {
                const content = getReportedContent(report);
                const isHidden = content?.participant?.result?.hidden;

                return (
                  <div
                    key={report.id}
                    className="bg-white/5 rounded-lg p-4 border border-white/10 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                        <span className="text-white font-medium capitalize">{report.reason.replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isHidden && (
                          <span className="px-2 py-1 rounded text-xs bg-orange-500/20 text-orange-400">
                            Hidden
                          </span>
                        )}
                        {getStatusBadge(report)}
                      </div>
                    </div>

                    {content?.participant?.result?.imageUrl && (
                      <div className="aspect-video rounded-lg overflow-hidden mb-3 bg-black/30">
                        <img
                          src={content.participant.result.imageUrl}
                          alt="Reported content"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    <div className="space-y-2 text-sm">
                      <p className="text-white/70">{report.description}</p>
                      <div className="flex justify-between text-white/50 text-xs">
                        <span>Reported by: {report.reportedBy.userName}</span>
                        <span>{new Date(report.createdAt).toLocaleDateString()}</span>
                      </div>

                      {report.action && (
                        <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                          <span className="text-white/50 text-xs">Action:</span>
                          {getActionBadge(report.action)}
                        </div>
                      )}

                      {report.adminNotes && (
                        <div className="pt-2 border-t border-white/10">
                          <p className="text-white/50 text-xs mb-1">Admin Notes:</p>
                          <p className="text-white/70 text-xs">{report.adminNotes}</p>
                        </div>
                      )}
                    </div>

                    {report.status === 'pending' && (
                      <div className="flex gap-2 mt-4">
                        {!isHidden ? (
                          <button
                            onClick={() => {
                              setSelectedReport(report);
                              handleHideContent(report);
                            }}
                            disabled={isProcessing}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors text-sm disabled:opacity-50"
                          >
                            <EyeOff className="w-4 h-4" />
                            Hide
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnhideContent(report)}
                            disabled={isProcessing}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors text-sm disabled:opacity-50"
                          >
                            <Eye className="w-4 h-4" />
                            Unhide
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedReport(report);
                            handleDeleteContent(report);
                          }}
                          disabled={isProcessing}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors text-sm disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                        <button
                          onClick={() => handleDismissReport(report)}
                          disabled={isProcessing}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 transition-colors text-sm disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
